import { type CustomEngineDef, compileCustom } from '../custom';
import type { Engine } from '../types';
import { chatgpt, claude, perplexity } from './ai';
import { amazon } from './amazon';
import { craigslist } from './craigslist';
import {
  arxiv,
  crates,
  grepapp,
  hackernews,
  npm,
  pypi,
  scholar,
  stackoverflow,
} from './dev';
import { ebay } from './ebay';
import { github } from './github';
import { google } from './google';
import { indeed, linkedin } from './jobs';
import { yelp } from './local';
import { marketplace } from './marketplace';
import { archive, imdb } from './media';
import { hipcamp, recreation } from './outdoors';
import { reddit } from './reddit';
import {
  bat,
  carscom,
  discogs,
  etsy,
  mercari,
  offerup,
  reverb,
  swappa,
} from './shops';
import { stubhub, ticketmaster } from './tickets';
import { airbnb, booking, kayak } from './travel';
import { walmart } from './walmart';
import { googleTranslate, merriamWebster } from './words';
import { youtube } from './youtube';

export const ENGINES: Engine[] = [
  google,
  youtube,
  ebay,
  craigslist,
  amazon,
  walmart,
  marketplace,
  mercari,
  offerup,
  etsy,
  reverb,
  discogs,
  swappa,
  carscom,
  bat,
  github,
  reddit,
  recreation,
  hipcamp,
  airbnb,
  booking,
  kayak,
  yelp,
  ticketmaster,
  stubhub,
  linkedin,
  indeed,
  stackoverflow,
  hackernews,
  grepapp,
  npm,
  pypi,
  crates,
  scholar,
  arxiv,
  merriamWebster,
  googleTranslate,
  imdb,
  archive,
  chatgpt,
  claude,
  perplexity,
];

/** Built-in bangs and aliases; custom engines may not reuse these. */
export const BUILTIN_NAMES = new Set(
  ENGINES.flatMap(e => [e.bang, ...e.aliases])
);

let custom: Engine[] = [];
let userAliases: Record<string, string[]> = {};
let byName = new Map<string, Engine>();
let byId = new Map<string, Engine>();

function index() {
  byName = new Map();
  // Built-ins last, so they win any clash.
  byId = new Map([...custom, ...ENGINES].map(e => [e.id, e]));
  // Your aliases first, so an engine's own bang always wins a clash.
  for (const [id, names] of Object.entries(userAliases)) {
    const engine = byId.get(id);
    if (engine) for (const name of names) byName.set(name, engine);
  }
  // Built-ins last so they win any clash that slipped past validation.
  for (const engine of [...custom, ...ENGINES]) {
    for (const name of [engine.bang, ...engine.aliases])
      byName.set(name, engine);
  }
}
index();

/** Registers the user's custom engines alongside the built-ins. */
export function setCustomEngines(defs: CustomEngineDef[]): void {
  custom = defs.map(compileCustom);
  index();
}

/** Registers your own aliases for engines (settings.engineAliases). */
export function setEngineAliases(aliases: Record<string, string[]> = {}): void {
  userAliases = aliases;
  index();
}

/** Built-in engines followed by custom ones. */
export function allEngines(): Engine[] {
  return [...ENGINES, ...custom];
}

export function findEngine(name: string): Engine | undefined {
  return byName.get(name.toLowerCase());
}

export function engineById(id: string): Engine | undefined {
  return byId.get(id);
}
